/**
 * Service for managing share requests and friend request approvals/rejections
 */

import type { ShareRequestNotification, FriendRequestNotification } from '../types';
import { useNotificationsStore } from './notificationsStore';
import { useBucketListStore } from './store';
import { doc, setDoc, updateDoc, getDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Create a share request notification and save to Firestore
 */
export const createShareRequestNotificationInFirestore = async (
  fromUserId: string,
  fromEmail: string,
  fromDisplayName: string | undefined,
  toUserId: string,
  itemId: string,
  itemTitle: string,
  shareRequestId: string
): Promise<ShareRequestNotification> => {
  const notification = createShareRequestNotification(
    fromUserId,
    fromEmail,
    fromDisplayName,
    toUserId,
    itemId,
    itemTitle,
    shareRequestId
  );

  // Save to Firestore in user's notification collection
  const notificationRef = doc(
    db,
    'users',
    toUserId,
    'notifications',
    notification.id
  );
  await setDoc(notificationRef, notification);

  return notification;
};

/**
 * Create a share request notification
 */
export const createShareRequestNotification = (
  fromUserId: string,
  fromEmail: string,
  fromDisplayName: string | undefined,
  toUserId: string,
  itemId: string,
  itemTitle: string,
  shareRequestId: string
): ShareRequestNotification => {
  return {
    id: `notif-share-${shareRequestId}-${Date.now()}`,
    type: 'share-request',
    userId: toUserId,
    fromUserId,
    fromEmail,
    fromDisplayName,
    itemId,
    itemTitle,
    shareRequestId,
    createdAt: new Date().toISOString(),
    read: false,
    status: 'unread',
  };
};

/**
 * Create a friend request notification
 */
export const createFriendRequestNotification = (
  fromUserId: string,
  fromEmail: string,
  fromDisplayName: string | undefined,
  toUserId: string,
  friendRequestId: string
): FriendRequestNotification => {
  return {
    id: `notif-friend-${friendRequestId}-${Date.now()}`,
    type: 'friend-request',
    userId: toUserId,
    fromUserId,
    fromEmail,
    fromDisplayName,
    friendRequestId,
    createdAt: new Date().toISOString(),
    read: false,
    status: 'unread',
  };
};

/**
 * Handle share request approval
 */
export const approveShareRequest = async (
  notification: ShareRequestNotification,
  currentUserId: string
) => {
  try {
    // Use arrayUnion to safely add user to sharedWith
    const itemRef = doc(db, 'bucketItems', notification.itemId);
    await updateDoc(itemRef, {
      sharedWith: arrayUnion(currentUserId)
    });

    // Get the fresh item from Firestore to ensure local store has correct data
    const itemSnapshot = await getDoc(itemRef);
    if (itemSnapshot.exists()) {
      const freshItem = itemSnapshot.data() as any;
      const store = useBucketListStore.getState();
      
      // If item already in local store, update it with fresh data
      if (store.getItemById(notification.itemId)) {
        store.updateItem(notification.itemId, {
          sharedWith: freshItem.sharedWith,
          ownerId: freshItem.ownerId,
        });
      } else {
        // Item not in local store yet, add it
        // Make sure ownerId is definitely set
        if (!freshItem.ownerId) {
          console.error('Item from Firestore missing ownerId!', notification.itemId);
        }
        store.addItem({
          ...freshItem,
          ownerId: freshItem.ownerId || '', // Explicitly set ownerId
        });
      }
    }

    // Remove notification from local store
    useNotificationsStore.getState().removeNotification(notification.id);

    return true;
  } catch (error) {
    console.error('Error approving share request:', error);
    throw error;
  }
};

/**
 * Handle share request rejection
 */
export const rejectShareRequest = async (
  notification: ShareRequestNotification
) => {
  try {
    // Just remove the notification - don't add to sharedWith
    useNotificationsStore.getState().removeNotification(notification.id);
    return true;
  } catch (error) {
    console.error('Error rejecting share request:', error);
    throw error;
  }
};

/**
 * Handle friend request approval
 */
export const approveFriendRequest = async (
  notification: FriendRequestNotification,
  currentUserId: string
) => {
  try {
    // Import here to avoid circular dependency
    const { friendsService } = await import('./friendsService');
    
    // Accept the friend request
    await friendsService.acceptFriendRequest(notification.friendRequestId, currentUserId);

    // Remove notification from local store
    useNotificationsStore.getState().removeNotification(notification.id);
    return true;
  } catch (error) {
    console.error('Error approving friend request:', error);
    throw error;
  }
};

/**
 * Handle friend request rejection
 */
export const rejectFriendRequest = async (
  notification: FriendRequestNotification
) => {
  try {
    // Import here to avoid circular dependency
    const { friendsService } = await import('./friendsService');
    
    // Reject the friend request
    await friendsService.rejectFriendRequest(notification.friendRequestId);

    // Remove notification from local store
    useNotificationsStore.getState().removeNotification(notification.id);
    return true;
  } catch (error) {
    console.error('Error rejecting friend request:', error);
    throw error;
  }
};
